import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Row,
  Col,
  Typography,
  Alert,
  Divider,
  Descriptions,
  message,
  Tooltip,
  Badge,
  Drawer,
  List,
  Collapse,
  Statistic,
  Empty,
  Popconfirm,
} from 'antd';
import {
  SettingOutlined,
  EditOutlined,
  SaveOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  StopOutlined,
  PlayCircleOutlined,
  SearchOutlined,
  FilterOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  EyeOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';

import { AppDispatch, RootState } from '../../../store/store';
import { STRRule, STRRuleThreshold } from '../../../services/strService/strService';
import {
  getAllSTRRulesThunk,
  getSTRRuleThunk,
  updateSTRRuleThresholdsThunk,
  toggleSTRRuleThunk,
} from '../../../services/strService/strThunk';
import { clearSelectedRule } from '../../../features/str/strSlice';

const { Text, Title } = Typography;
const { Option } = Select;
const { Panel } = Collapse;
const { Search } = Input;

const STRRuleConfiguration: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { rules, selectedRule } = useSelector((state: RootState) => state.str);

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [selectedRuleForDetails, setSelectedRuleForDetails] = useState<STRRule | null>(null);
  const [searchText, setSearchText] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(getAllSTRRulesThunk());
  }, [dispatch]);

  const handleConfigureRule = async (ruleCode: string) => {
    try {
      await dispatch(getSTRRuleThunk(ruleCode));
      setShowConfigModal(true);
    } catch (error) {
      message.error('Failed to fetch rule configuration');
    }
  };

  const handleViewDetails = (rule: STRRule) => {
    setSelectedRuleForDetails(rule);
    setShowDetailsDrawer(true);
  };

  const handleToggleRule = async (ruleCode: string, isEnabled: boolean) => {
    try {
      await dispatch(toggleSTRRuleThunk({ ruleCode, isEnabled }));
      message.success(`Rule ${isEnabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      message.error('Failed to toggle rule');
    }
  };

  const handleBulkToggle = async (enabled: boolean) => {
    const selectedRules = rules.data.filter((rule) => selectedRowKeys.includes(rule.ruleCode));

    try {
      for (const rule of selectedRules) {
        await dispatch(toggleSTRRuleThunk({ ruleCode: rule.ruleCode, isEnabled: enabled }));
      }
      setSelectedRowKeys([]);
      message.success(
        `${selectedRules.length} rules ${enabled ? 'enabled' : 'disabled'} successfully`
      );
    } catch (error) {
      message.error('Failed to bulk toggle rules');
    }
  };

  const handleUpdateThresholds = async (values: any) => {
    if (!selectedRule.data) return;

    const thresholds = Object.entries(values).map(([key, value]: [string, any]) => ({
      thresholdKey: key,
      thresholdValue: value,
      dataType: getDataTypeForThreshold(key) as 'DECIMAL' | 'INTEGER' | 'BOOLEAN' | 'STRING',
    }));

    try {
      await dispatch(
        updateSTRRuleThresholdsThunk({
          ruleCode: selectedRule.data.ruleCode,
          thresholds: { thresholds },
        })
      );
      setShowConfigModal(false);
      dispatch(clearSelectedRule());
      form.resetFields();
      message.success('Rule thresholds updated successfully');
      dispatch(getAllSTRRulesThunk()); // Refresh data
    } catch (error) {
      message.error('Failed to update rule thresholds');
    }
  };

  const getDataTypeForThreshold = (key: string): string => {
    const decimalKeys = [
      'percentage',
      'upperPercentage',
      'lowerPercentage',
      'installmentPercentage',
      'tenurePercentage',
      'minAmount',
    ];
    const integerKeys = [
      'limit',
      'dailyLimit',
      'weeklyLimit',
      'monthlyLimit',
      'timeWindow',
      'minPayments',
      'maxDefaults',
      'roundingDivisor',
    ];

    if (decimalKeys.some((k) => key.toLowerCase().includes(k.toLowerCase()))) return 'DECIMAL';
    if (integerKeys.some((k) => key.toLowerCase().includes(k.toLowerCase()))) return 'INTEGER';
    return 'INTEGER'; // default
  };

  const getRuleDescription = (ruleCode: string) => {
    const descriptions = {
      PAT001:
        'Monitors payments that exceed upper or lower thresholds relative to monthly installment',
      PFR001: 'Monitors frequency of transactions over daily, weekly, and monthly periods',
      FRP001:
        'Detects multiple smaller payments within short timeframes that combine to significant amounts',
      CBT001: 'Monitors payments from foreign jurisdictions and high-risk countries',
      CIC001: 'Validates payer name consistency against account holder information',
      TPP001: 'Monitors payments from different banks than the disbursement bank',
      RPR001: 'Flags early loan settlements before significant tenure completion',
      FNR001: 'Detects complex payment routing patterns that may indicate fund manipulation',
      FRD001: 'Monitors accounts with frequent default history and NPL transitions',
      UDN001: 'Identifies non-standard payment amounts and unusual denomination patterns',
    };
    return descriptions[ruleCode as keyof typeof descriptions] || 'No description available';
  };

  const getRuleStats = () => {
    const total = rules.data.length;
    const enabled = rules.data.filter((rule) => rule.isEnabled).length;
    const disabled = total - enabled;
    const high = rules.data.filter(
      (rule) => (rule.severity || rule.defaultSeverity) === 'HIGH'
    ).length;
    const medium = rules.data.filter(
      (rule) => (rule.severity || rule.defaultSeverity) === 'MEDIUM'
    ).length;
    const low = rules.data.filter(
      (rule) => (rule.severity || rule.defaultSeverity) === 'LOW'
    ).length;

    return { total, enabled, disabled, high, medium, low };
  };

  const filteredRules = rules.data.filter((rule) => {
    const matchesSearch =
      rule.ruleCode.toLowerCase().includes(searchText.toLowerCase()) ||
      rule.ruleName.toLowerCase().includes(searchText.toLowerCase());
    const matchesSeverity =
      !severityFilter || (rule.severity || rule.defaultSeverity) === severityFilter;
    const matchesStatus =
      !statusFilter ||
      (statusFilter === 'enabled' && rule.isEnabled) ||
      (statusFilter === 'disabled' && !rule.isEnabled);

    return matchesSearch && matchesSeverity && matchesStatus;
  });

  const renderThresholdConfig = () => {
    const rule = selectedRule.data;
    if (!rule) return null;

    // Populate form with existing threshold values
    const initialValues: Record<string, any> = {};
    rule.thresholds?.forEach((threshold) => {
      initialValues[threshold.thresholdKey] = threshold.thresholdValue;
    });

    const getFormFields = () => {
      // Use existing thresholds from the rule to generate form fields dynamically
      if (!rule.thresholds || rule.thresholds.length === 0) {
        return (
          <Alert
            message="Configuration Not Available"
            description="This rule does not have configurable thresholds or the configuration is not yet implemented."
            type="info"
            showIcon
          />
        );
      }

      // Generate form fields from existing thresholds
      return rule.thresholds.map((threshold: any) => {
        const fieldConfig = getFieldConfig(threshold.thresholdKey);

        return (
          <Form.Item
            key={threshold.thresholdKey}
            label={fieldConfig.label}
            name={threshold.thresholdKey}
            rules={[{ required: true, message: `Please enter ${fieldConfig.label.toLowerCase()}` }]}
          >
            <InputNumber
              min={fieldConfig.min}
              max={fieldConfig.max}
              placeholder={`Enter ${fieldConfig.label.toLowerCase()}`}
              addonAfter={fieldConfig.suffix}
              style={{ width: '100%' }}
            />
          </Form.Item>
        );
      });
    };

    const getFieldConfig = (thresholdKey: string) => {
      // Generate user-friendly labels and configurations based on threshold key
      const keyMappings: Record<string, any> = {
        upperPercentage: { label: 'Upper Threshold Percentage', min: 0, max: 100, suffix: '%' },
        lowerPercentage: { label: 'Lower Threshold Percentage', min: 0, max: 100, suffix: '%' },
        tenurePercentage: { label: 'Tenure Percentage', min: 0, max: 100, suffix: '%' },
        installmentPercentage: { label: 'Installment Percentage', min: 0, max: 100, suffix: '%' },
        dailyLimit: { label: 'Daily Limit', min: 1, suffix: 'payments' },
        weeklyLimit: { label: 'Weekly Limit', min: 1, suffix: 'payments' },
        monthlyLimit: { label: 'Monthly Limit', min: 1, suffix: 'payments' },
        timeWindow: { label: 'Time Window', min: 1, suffix: 'hours' },
        minPayments: { label: 'Minimum Payments', min: 2, suffix: 'payments' },
        maxDefaults: { label: 'Maximum Defaults', min: 1, suffix: 'defaults' },
        roundingDivisor: { label: 'Rounding Divisor', min: 1, suffix: '' },
        minAmount: { label: 'Minimum Amount', min: 0, suffix: 'RM' },
      };

      // If we have a specific mapping, use it
      if (keyMappings[thresholdKey]) {
        return keyMappings[thresholdKey];
      }

      // Otherwise, generate a generic configuration
      const label = thresholdKey
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase());

      return {
        label,
        min: 0,
        suffix: thresholdKey.toLowerCase().includes('percentage') ? '%' : '',
      };
    };

    return (
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <SettingOutlined />
            <span>
              Configure {rule.ruleCode} - {rule.ruleName}
            </span>
          </div>
        }
        open={showConfigModal}
        onOk={() => form.submit()}
        onCancel={() => {
          setShowConfigModal(false);
          dispatch(clearSelectedRule());
          form.resetFields();
        }}
        okText="Save Configuration"
        cancelText="Cancel"
        width={700}
        confirmLoading={selectedRule.isLoading}
      >
        <div className="space-y-4">
          <Alert message={getRuleDescription(rule.ruleCode)} type="info" showIcon />

          <Form
            form={form}
            layout="vertical"
            onFinish={handleUpdateThresholds}
            initialValues={initialValues}
          >
            <Row gutter={16}>
              <Col span={24}>{getFormFields()}</Col>
            </Row>
          </Form>
        </div>
      </Modal>
    );
  };

  const renderDetailsDrawer = () => {
    if (!selectedRuleForDetails) return null;

    return (
      <Drawer
        title={
          <div className="flex items-center space-x-2">
            <InfoCircleOutlined />
            <span>
              {selectedRuleForDetails.ruleCode} - {selectedRuleForDetails.ruleName}
            </span>
          </div>
        }
        width={600}
        open={showDetailsDrawer}
        onClose={() => {
          setShowDetailsDrawer(false);
          setSelectedRuleForDetails(null);
        }}
      >
        <div className="space-y-6">
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Rule Code">
              <Tag color="blue">{selectedRuleForDetails.ruleCode}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Rule Name">
              {selectedRuleForDetails.ruleName}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge
                status={selectedRuleForDetails.isEnabled ? 'processing' : 'error'}
                text={selectedRuleForDetails.isEnabled ? 'Enabled' : 'Disabled'}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Severity">
              <Tag
                color={
                  (selectedRuleForDetails.severity || selectedRuleForDetails.defaultSeverity) ===
                  'HIGH'
                    ? 'red'
                    : (selectedRuleForDetails.severity ||
                        selectedRuleForDetails.defaultSeverity) === 'MEDIUM'
                    ? 'orange'
                    : 'green'
                }
              >
                {selectedRuleForDetails.severity || selectedRuleForDetails.defaultSeverity}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {getRuleDescription(selectedRuleForDetails.ruleCode)}
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <div>
            <Title level={5}>Configured Thresholds</Title>
            {selectedRuleForDetails.thresholds && selectedRuleForDetails.thresholds.length > 0 ? (
              <List
                dataSource={selectedRuleForDetails.thresholds}
                renderItem={(threshold) => (
                  <List.Item>
                    <div className="w-full">
                      <div className="flex justify-between items-center">
                        <Text strong>{threshold.thresholdKey}</Text>
                        <div className="flex items-center space-x-2">
                          <Text>{threshold.thresholdValue}</Text>
                          <Tag style={{ fontSize: '11px' }}>{threshold.dataType}</Tag>
                        </div>
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No thresholds configured" />
            )}
          </div>
        </div>
      </Drawer>
    );
  };

  const columns: ColumnsType<STRRule> = [
    {
      title: 'Rule Code',
      dataIndex: 'ruleCode',
      key: 'ruleCode',
      width: 120,
      fixed: 'left',
      render: (ruleCode: string) => (
        <Tag color="blue" style={{ fontWeight: 'bold' }}>
          {ruleCode}
        </Tag>
      ),
    },
    {
      title: 'Rule Name',
      dataIndex: 'ruleName',
      key: 'ruleName',
      width: 200,
      render: (ruleName: string) => <Text strong>{ruleName}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      width: 120,
      render: (isEnabled: boolean, record: STRRule) => (
        <div className="flex items-center space-x-2">
          <Badge
            status={isEnabled ? 'processing' : 'error'}
            text={isEnabled ? 'Enabled' : 'Disabled'}
          />
          <Switch
            checked={isEnabled}
            onChange={(checked) => handleToggleRule(record.ruleCode, checked)}
            size="small"
          />
        </div>
      ),
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (severity: string, record: STRRule) => {
        const actualSeverity = severity || record.defaultSeverity || 'LOW';
        return (
          <Tag
            color={
              actualSeverity === 'HIGH' ? 'red' : actualSeverity === 'MEDIUM' ? 'orange' : 'green'
            }
            icon={
              actualSeverity === 'HIGH' ? (
                <ExclamationCircleOutlined />
              ) : actualSeverity === 'MEDIUM' ? (
                <WarningOutlined />
              ) : (
                <InfoCircleOutlined />
              )
            }
          >
            {actualSeverity}
          </Tag>
        );
      },
    },
    {
      title: 'Thresholds',
      dataIndex: 'thresholds',
      key: 'thresholds',
      width: 120,
      render: (thresholds: STRRuleThreshold[]) => (
        <div className="flex items-center space-x-1">
          <Text>{thresholds?.length || 0}</Text>
          <Text type="secondary">configured</Text>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record: STRRule) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)} />
          </Tooltip>
          <Tooltip title="Configure Thresholds">
            <Button
              size="small"
              icon={<SettingOutlined />}
              onClick={() => handleConfigureRule(record.ruleCode)}
              disabled={!record.thresholds || record.thresholds.length === 0}
            />
          </Tooltip>
          <Tooltip title={record.isEnabled ? 'Disable Rule' : 'Enable Rule'}>
            <Button
              size="small"
              icon={record.isEnabled ? <StopOutlined /> : <PlayCircleOutlined />}
              onClick={() => handleToggleRule(record.ruleCode, !record.isEnabled)}
              danger={record.isEnabled}
              type={record.isEnabled ? 'default' : 'primary'}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const stats = getRuleStats();

  return (
    <div className="space-y-4">
      {/* Statistics Cards */}
      <Row gutter={16}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Total Rules"
              value={stats.total}
              valueStyle={{ color: '#1890ff' }}
              prefix={<SettingOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Enabled"
              value={stats.enabled}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Disabled"
              value={stats.disabled}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<StopOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="High Severity"
              value={stats.high}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Card */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <Title level={4}>STR Rule Configuration</Title>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => dispatch(getAllSTRRulesThunk())}
              loading={rules.isLoading}
            >
              Refresh
            </Button>
          </Space>
        </div>

        {/* Filters and Search */}
        <div className="mb-4">
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Search
                placeholder="Search rules..."
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined />}
              />
            </Col>
            <Col xs={24} sm={6} md={4}>
              <Select
                placeholder="Filter by severity"
                allowClear
                style={{ width: '100%' }}
                value={severityFilter}
                onChange={setSeverityFilter}
              >
                <Option value="HIGH">High</Option>
                <Option value="MEDIUM">Medium</Option>
                <Option value="LOW">Low</Option>
              </Select>
            </Col>
            <Col xs={24} sm={6} md={4}>
              <Select
                placeholder="Filter by status"
                allowClear
                style={{ width: '100%' }}
                value={statusFilter}
                onChange={setStatusFilter}
              >
                <Option value="enabled">Enabled</Option>
                <Option value="disabled">Disabled</Option>
              </Select>
            </Col>
          </Row>
        </div>

        {/* Bulk Actions */}
        {selectedRowKeys.length > 0 && (
          <div className="mb-4">
            <Alert
              message={`${selectedRowKeys.length} rule(s) selected`}
              type="info"
              action={
                <Space>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => handleBulkToggle(true)}
                    icon={<PlayCircleOutlined />}
                  >
                    Enable Selected
                  </Button>
                  <Button
                    size="small"
                    danger
                    onClick={() => handleBulkToggle(false)}
                    icon={<StopOutlined />}
                  >
                    Disable Selected
                  </Button>
                </Space>
              }
              showIcon
            />
          </div>
        )}

        <Alert
          message="Rule Configuration Guidelines"
          description="Configure thresholds and parameters for STR monitoring rules. Changes will take effect immediately for new transactions. Test thoroughly before enabling rules in production."
          type="info"
          showIcon
          className="mb-4"
        />

        <Table
          columns={columns}
          dataSource={filteredRules}
          rowKey="ruleCode"
          rowSelection={rowSelection}
          loading={rules.isLoading}
          pagination={{
            total: filteredRules.length,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rules`,
          }}
          scroll={{ x: 1000 }}
          size="small"
        />
      </Card>

      {renderThresholdConfig()}
      {renderDetailsDrawer()}
    </div>
  );
};

export default STRRuleConfiguration;
